import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useForm } from 'react-hook-form';
import AdminLayout from '../../components/Layout/AdminLayout';
import {
    UserPlus,
    Mail,
    Copy,
    Check,
    Clock,
    CheckCircle,
    Search,
    Filter,
    Download,
    Trash2
} from 'lucide-react';
import axios from 'axios';
import toast from 'react-hot-toast';

const StudentManagement = () => {
    const [students, setStudents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showInviteForm, setShowInviteForm] = useState(false);
    const [inviting, setInviting] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [copiedUrl, setCopiedUrl] = useState(null);

    const { register, handleSubmit, formState: { errors }, reset } = useForm();

    useEffect(() => {
        fetchStudents();
    }, []);

    const fetchStudents = async () => {
        try {
            const response = await axios.get('/api/admin/students');
            setStudents(response.data);
        } catch (error) {
            toast.error('Failed to load students');
            console.error('Students fetch error:', error);
        } finally {
            setLoading(false);
        }
    };

    const onInviteSubmit = async (data) => {
        setInviting(true);

        try {
            const response = await axios.post('/api/admin/students/invite', data);
            toast.success(`Invitation sent to ${data.name}`);
            setStudents([response.data.student, ...students]);
            setShowInviteForm(false);
            reset();
        } catch (error) {
            const message = error.response?.data?.message || 'Failed to send invitation';
            toast.error(message);
        } finally {
            setInviting(false);
        }
    };

    const copyAssessmentUrl = async (token) => {
        const url = `${window.location.origin}/assessment/${token}`;

        try {
            await navigator.clipboard.writeText(url);
            setCopiedUrl(token);
            toast.success('Assessment URL copied to clipboard');
            setTimeout(() => setCopiedUrl(null), 2000);
        } catch (error) {
            toast.error('Failed to copy URL');
        }
    };

    const deleteStudent = async (studentId, studentName) => {
        if (!window.confirm(`Are you sure you want to delete ${studentName}? This action cannot be undone.`)) {
            return;
        }

        try {
            await axios.delete(`/api/admin/students/${studentId}`);
            toast.success('Student deleted successfully');
            fetchStudents(); // Refresh the list
        } catch (error) {
            const message = error.response?.data?.message || 'Failed to delete student';
            toast.error(message);
            console.error('Delete student error:', error);
        }
    };

    const resendAssessment = async (studentId, studentName) => {
        if (!window.confirm(`Resend assessment invitation to ${studentName}?`)) {
            return;
        }

        try {
            await axios.post(`/api/admin/students/${studentId}/resend-assessment`);
            toast.success('Assessment invitation resent successfully');
        } catch (error) {
            const message = error.response?.data?.message || 'Failed to resend assessment invitation';
            toast.error(message);
        }
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case 'invited':
                return <Clock size={16} className="text-warning" />;
            case 'started':
                return <Clock size={16} className="text-primary" />;
            case 'completed':
                return <CheckCircle size={16} className="text-success" />;
            default:
                return null;
        }
    };

    const getStatusText = (status) => {
        switch (status) {
            case 'invited':
                return 'Invited';
            case 'started':
                return 'In Progress';
            case 'completed':
                return 'Completed';
            default:
                return status;
        }
    };

    const canResend = (student) => {
        if (student.status === 'invited' || student.status === 'started') {
            return true;
        }
        return !(student.lastResult && student.lastResult.passed === true);
    };

    const filteredStudents = students.filter(student => {
        const matchesSearch = student.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            student.email.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesStatus = statusFilter === 'all' || student.status === statusFilter;
        return matchesSearch && matchesStatus;
    });

    if (loading) {
        return (
            <AdminLayout title="Student Management">
                <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                    <div className="loading-spinner"></div>
                </div>
            </AdminLayout>
        );
    }

    return (
        <AdminLayout title="Student Management">
            <div className="student-management">
                {/* Header Actions */}
                <div className="page-header">
                    <div className="header-actions">
                        <button
                            className="btn btn-primary"
                            onClick={() => setShowInviteForm(true)}
                        >
                            <UserPlus size={20} />
                            Invite Student
                        </button>
                    </div>
                </div>

                {/* Invite Form Modal */}
                {showInviteForm && (
                    <div className="modal-overlay">
                        <motion.div
                            className="modal"
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.9 }}
                        >
                            <div className="modal-header">
                                <h3>Invite Student</h3>
                                <button
                                    className="modal-close"
                                    onClick={() => setShowInviteForm(false)}
                                >
                                    ×
                                </button>
                            </div>

                            <form onSubmit={handleSubmit(onInviteSubmit)} className="modal-content">
                                <div className="form-group">
                                    <label htmlFor="name" className="form-label">Full Name</label>
                                    <input
                                        id="name"
                                        type="text"
                                        className={`form-control ${errors.name ? 'error' : ''}`}
                                        placeholder="Enter student's full name"
                                        {...register('name', {
                                            required: 'Name is required',
                                            minLength: {
                                                value: 2,
                                                message: 'Name must be at least 2 characters'
                                            }
                                        })}
                                    />
                                    {errors.name && (
                                        <div className="error-message">{errors.name.message}</div>
                                    )}
                                </div>

                                <div className="form-group">
                                    <label htmlFor="email" className="form-label">Email Address</label>
                                    <input
                                        id="email"
                                        type="email"
                                        className={`form-control ${errors.email ? 'error' : ''}`}
                                        placeholder="Enter student's email"
                                        {...register('email', {
                                            required: 'Email is required',
                                            pattern: {
                                                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                                                message: 'Invalid email address'
                                            }
                                        })}
                                    />
                                    {errors.email && (
                                        <div className="error-message">{errors.email.message}</div>
                                    )}
                                </div>

                                <div className="form-group">
                                    <label htmlFor="phoneNumber" className="form-label">Phone Number (Optional)</label>
                                    <input
                                        id="phoneNumber"
                                        type="tel"
                                        className="form-control"
                                        placeholder="Enter phone number"
                                        {...register('phoneNumber')}
                                    />
                                </div>

                                <div className="modal-actions">
                                    <button
                                        type="button"
                                        className="btn btn-secondary"
                                        onClick={() => setShowInviteForm(false)}
                                        disabled={inviting}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        className="btn btn-primary"
                                        disabled={inviting}
                                    >
                                        {inviting ? (
                                            <div className="loading-spinner" style={{ width: '16px', height: '16px' }} />
                                        ) : (
                                            <>
                                                <Mail size={16} />
                                                Send Invitation
                                            </>
                                        )}
                                    </button>
                                </div>
                            </form>
                        </motion.div>
                    </div>
                )}

                {/* Filters */}
                <div className="filters-section">
                    <div className="search-box">
                        <Search size={20} />
                        <input
                            type="text"
                            placeholder="Search students..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>

                    <div className="filter-select">
                        <Filter size={20} />
                        <select
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                        >
                            <option value="all">All Status</option>
                            <option value="invited">Invited</option>
                            <option value="started">In Progress</option>
                            <option value="completed">Completed</option>
                        </select>
                    </div>
                </div>

                {/* Students Table */}
                <div className="card">
                    <div className="card-header">
                        <h2 className="card-title">Students ({filteredStudents.length})</h2>
                    </div>

                    {filteredStudents.length > 0 ? (
                        <div className="table-container">
                            <table className="students-table">
                                <thead>
                                    <tr>
                                        <th>Student</th>
                                        <th>Status</th>
                                        <th>Invited Date</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredStudents.map((student, index) => (
                                        <motion.tr
                                            key={student._id}
                                            initial={{ opacity: 0, y: 20 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ delay: index * 0.05 }}
                                        >
                                            <td>
                                                <div className="student-info">
                                                    <h4>{student.name}</h4>
                                                    <p>{student.email}</p>
                                                    {student.phoneNumber && (
                                                        <p className="phone">{student.phoneNumber}</p>
                                                    )}
                                                    <div className="invited-by">
                                                        {student.source === 'mgmt'
                                                            ? 'Invited by: mgmt'
                                                            : `Invited by: ${student.invitedBy?.name || '—'}`}
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                <div className="status-badge">
                                                    {getStatusIcon(student.status)}
                                                    <span>{getStatusText(student.status)}</span>
                                                </div>
                                            </td>
                                            <td>
                                                <div className="date-info">
                                                    {new Date(student.createdAt).toLocaleDateString()}
                                                </div>
                                            </td>
                                            <td>
                                                <div className="action-buttons">
                                                    {student.inviteToken && (
                                                        <button
                                                            className="btn btn-sm btn-outline"
                                                            onClick={() => copyAssessmentUrl(student.inviteToken)}
                                                            title="Copy assessment URL"
                                                        >
                                                            {copiedUrl === student.inviteToken ? (
                                                                <Check size={16} />
                                                            ) : (
                                                                <Copy size={16} />
                                                            )}
                                                        </button>
                                                    )}
                                                    {canResend(student) && (
                                                        <button
                                                            className="btn btn-sm btn-primary"
                                                            onClick={() => resendAssessment(student._id, student.name)}
                                                            title="Resend Assessment Invitation"
                                                        >
                                                            <Mail size={16} />
                                                        </button>
                                                    )}
                                                    <button
                                                        className="btn btn-sm btn-danger"
                                                        onClick={() => deleteStudent(student._id, student.name)}
                                                        title="Delete student"
                                                    >
                                                        <Trash2 size={16} />
                                                    </button>
                                                </div>
                                            </td>
                                        </motion.tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <div className="empty-state">
                            <UserPlus size={48} className="text-muted" />
                            <h3>No students found</h3>
                            <p>
                                {searchTerm || statusFilter !== 'all'
                                    ? 'Try adjusting your search or filter criteria.'
                                    : 'Start by inviting students to take the assessment.'
                                }
                            </p>
                            {!searchTerm && statusFilter === 'all' && (
                                <button
                                    className="btn btn-primary mt-3"
                                    onClick={() => setShowInviteForm(true)}
                                >
                                    <UserPlus size={20} />
                                    Invite First Student
                                </button>
                            )}
                        </div>
                    )}
                </div>
            </div>

            <style jsx>{`
        .student-management {
          max-width: 1200px;
          margin: 0 auto;
        }

        .page-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 24px;
        }

        .header-actions {
          display: flex;
          gap: 12px;
        }

        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          padding: 20px;
        }

        .modal {
          background-color: var(--white);
          border-radius: var(--border-radius-lg);
          box-shadow: var(--shadow-lg);
          width: 100%;
          max-width: 500px;
          max-height: 90vh;
          overflow-y: auto;
        }

        .modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 24px 24px 0 24px;
          margin-bottom: 20px;
        }

        .modal-header h3 {
          margin: 0;
          color: var(--text-primary);
        }

        .modal-close {
          background: none;
          border: none;
          font-size: 24px;
          color: var(--text-secondary);
          cursor: pointer;
          width: 32px;
          height: 32px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: var(--border-radius);
          transition: var(--transition);
        }

        .modal-close:hover {
          background-color: var(--gray-100);
          color: var(--text-primary);
        }

        .modal-content {
          padding: 0 24px 24px 24px;
        }

        .modal-actions {
          display: flex;
          gap: 12px;
          justify-content: flex-end;
          margin-top: 24px;
          padding-top: 20px;
          border-top: 1px solid var(--gray-200);
        }

        .filters-section {
          display: flex;
          gap: 16px;
          margin-bottom: 24px;
          align-items: center;
        }

        .search-box {
          position: relative;
          flex: 1;
          max-width: 400px;
        }

        .search-box svg {
          position: absolute;
          left: 12px;
          top: 50%;
          transform: translateY(-50%);
          color: var(--text-secondary);
        }

        .search-box input {
          width: 100%;
          padding: 12px 16px 12px 44px;
          border: 2px solid var(--gray-300);
          border-radius: var(--border-radius);
          font-size: 14px;
          transition: var(--transition);
        }

        .search-box input:focus {
          outline: none;
          border-color: var(--primary-accent);
        }

        .filter-select {
          position: relative;
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .filter-select svg {
          color: var(--text-secondary);
        }

        .filter-select select {
          padding: 12px 16px;
          border: 2px solid var(--gray-300);
          border-radius: var(--border-radius);
          font-size: 14px;
          background-color: var(--white);
          cursor: pointer;
          transition: var(--transition);
        }

        .filter-select select:focus {
          outline: none;
          border-color: var(--primary-accent);
        }

        .table-container {
          overflow-x: auto;
        }

        .students-table {
          width: 100%;
          border-collapse: collapse;
        }

        .students-table th {
          text-align: left;
          padding: 16px 20px;
          background-color: var(--gray-100);
          color: var(--text-primary);
          font-weight: 600;
          font-size: 14px;
          border-bottom: 1px solid var(--gray-200);
        }

        .students-table td {
          padding: 16px 20px;
          border-bottom: 1px solid var(--gray-200);
          vertical-align: top;
        }

        .student-info h4 {
          margin: 0 0 4px 0;
          font-size: 16px;
          font-weight: 600;
          color: var(--text-primary);
        }

        .student-info p {
          margin: 0;
          font-size: 14px;
          color: var(--text-secondary);
        }

        .student-info .phone {
          font-size: 13px;
          color: var(--text-secondary);
          opacity: 0.8;
        }

        .student-info .invited-by {
          font-size: 13px;
          color: var(--text-secondary);
          opacity: 0.8;
        }

        .status-badge {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 14px;
          font-weight: 500;
        }

        .date-info {
          font-size: 14px;
          color: var(--text-secondary);
        }

        .action-buttons {
          display: flex;
          gap: 8px;
        }

        .empty-state {
          text-align: center;
          padding: 60px 20px;
        }

        .empty-state h3 {
          margin: 16px 0 8px 0;
          color: var(--text-primary);
        }

        .empty-state p {
          color: var(--text-secondary);
          margin: 0;
        }

        @media (max-width: 768px) {
          .filters-section {
            flex-direction: column;
            align-items: stretch;
          }

          .search-box {
            max-width: none;
          }

          .table-container {
            border-radius: var(--border-radius);
            border: 1px solid var(--gray-200);
          }

          .students-table th,
          .students-table td {
            padding: 12px 16px;
          }

          .modal {
            margin: 20px;
            max-width: none;
          }
        }
      `}</style>
        </AdminLayout>
    );
};

export default StudentManagement;
